import { rd, type RemoteData } from '@passionware/monads';
import { X } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { Input } from 'v5/design-system/Input';
import { ListBoxSkeleton } from 'v5/design-system/ListBox';
import { DndList } from 'v5/patterns/dnd/DndList';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  SectionedChecklist,
  type SectionedChecklistSection
} from 'v5/patterns/widgets/SectionedChecklist';
import { cn } from 'v5/platform/dom/cn';
import {
  DIVIDER_KEY,
  fieldOrderUtils,
  filterSections
} from './fieldChooser.logic';
import { FieldChooserColumn } from './FieldChooserColumn';

/** What one change did to the picked fields. */
export interface FieldPick {
  added: string[];
  removed: string[];
}

/** A line the user drags among the picked ones; the ones above it are the host's first ones. */
export interface FieldDivider {
  /** How many picked fields sit above the line. */
  at: number;
  label: string;
}

interface FieldChooserBaseProps {
  /** The fields to pick from, in titled sections. */
  catalog: RemoteData<ReadonlyArray<SectionedChecklistSection>>;
  /** Names the catalog's column and list. */
  catalogLabel: string;
  /** Shown when the catalog has no fields at all. */
  emptyCatalog: ReactNode;
  /** The picked keys, in order. */
  picked: ReadonlyArray<string>;
  labelOf: (key: string) => string;
  /**
   * Fields ticked, unticked or removed from the order, once per change (a Shift range ticks
   * several); `added` in the catalog's order.
   */
  onPick: (change: FieldPick) => void;
  /** Once this many are picked, the rest of the catalog waits. */
  limit?: number;
  pickedTitle: ReactNode;
  pickedMeta?: ReactNode;
  /** Names the ordered list. */
  orderLabel: string;
  /** Shown instead of the order while nothing is picked. */
  emptyPicked: ReactNode;
}

export type FieldChooserProps = FieldChooserBaseProps &
  (
    | {
        divider?: undefined;
        /** The order after a drag. */
        onReorder: (picked: string[]) => void;
      }
    | {
        divider: FieldDivider;
        /** The order after a drag, and where the line was dropped. */
        onReorder: (picked: string[], dividerAt: number) => void;
      }
  );

/**
 * Picks fields from a catalog and orders them: the catalog with its search in one column, the
 * picked ones in order in the next. Renders the two columns for the host's grid, which may add
 * its own beside them (a preview).
 */
export function FieldChooser({
  catalog,
  catalogLabel,
  emptyCatalog,
  picked,
  labelOf,
  onPick,
  onReorder,
  limit,
  pickedTitle,
  pickedMeta,
  orderLabel,
  emptyPicked,
  divider
}: FieldChooserProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'FieldChooser' });
  const [query, setQuery] = useState('');
  const pickedTitleId = useId();
  return (
    <>
      <FieldChooserColumn
        aria-label={catalogLabel}
        header={
          <Input
            size="sm"
            aria-label={t('search')}
            placeholder={t('search')}
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        }
      >
        {rd
          .journey(catalog)
          .wait(
            <ListBoxSkeleton
              numRows={8}
              showIcon={false}
              showSecondary={false}
              showExtra={false}
            />
          )
          .catch(renderError)
          .map(sections => (
            <Catalog
              sections={sections}
              query={query}
              label={catalogLabel}
              emptyCatalog={emptyCatalog}
              picked={picked}
              full={limit !== undefined && picked.length >= limit}
              onPick={onPick}
            />
          ))}
      </FieldChooserColumn>
      <FieldChooserColumn
        aria-labelledby={pickedTitleId}
        header={
          <div className="flex h-9 items-center gap-2">
            <h3 id={pickedTitleId} className="text-base font-semibold">
              {pickedTitle}
            </h3>
            {pickedMeta && (
              <span className="text-sm font-medium text-fg-muted">
                {pickedMeta}
              </span>
            )}
          </div>
        }
      >
        {picked.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-default px-4 py-8 text-center text-sm text-fg-muted">
            {emptyPicked}
          </p>
        ) : (
          <Order
            picked={picked}
            labelOf={labelOf}
            label={orderLabel}
            divider={divider}
            onRemove={key => onPick({ added: [], removed: [key] })}
            onReorder={onReorder}
          />
        )}
      </FieldChooserColumn>
    </>
  );
}

/** The catalog as the design system's checklist, ticked for the picked fields; full, the rest wait. */
function Catalog({
  sections,
  query,
  label,
  emptyCatalog,
  picked,
  full,
  onPick
}: {
  sections: ReadonlyArray<SectionedChecklistSection>;
  query: string;
  label: string;
  emptyCatalog: ReactNode;
  picked: ReadonlyArray<string>;
  full: boolean;
  onPick: (change: FieldPick) => void;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'FieldChooser' });
  const shown = useMemo(
    () => filterSections(sections, query),
    [sections, query]
  );
  return (
    <SectionedChecklist
      aria-label={label}
      className="-mx-4"
      sections={shown}
      selected={new Set(picked)}
      onChange={next =>
        onPick({
          added: [...next].filter(key => !picked.includes(key)),
          removed: picked.filter(key => !next.has(key))
        })
      }
      disabledIds={
        full
          ? shown
              .flatMap(section => section.items)
              .filter(item => !picked.includes(item.id))
              .map(item => item.id)
          : undefined
      }
      emptyState={
        sections.length === 0
          ? emptyCatalog
          : t('noMatches', { query: query.trim() })
      }
    />
  );
}

/** The picked fields in order, with the line among them when the host has one. */
function Order({
  picked,
  labelOf,
  label,
  divider,
  onRemove,
  onReorder
}: {
  picked: ReadonlyArray<string>;
  labelOf: (key: string) => string;
  label: string;
  divider: FieldChooserProps['divider'];
  onRemove: (key: string) => void;
  onReorder: FieldChooserProps['onReorder'];
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'FieldChooser' });
  const keys = divider
    ? fieldOrderUtils.withDivider(picked, divider.at)
    : picked;
  return (
    <DndList
      items={keys.map(key => ({ key }))}
      getKey={item => item.key}
      getTextValue={item =>
        item.key === DIVIDER_KEY && divider
          ? divider.label
          : labelOf(String(item.key))
      }
      ariaLabel={label}
      onReorder={ordered => {
        const next = fieldOrderUtils.split(ordered);
        onReorder(next.picked, next.dividerAt);
      }}
      className="flex flex-col gap-2"
      gap="2"
      itemClassName="rounded-lg"
    >
      {(item, kit) => {
        if (item.key === DIVIDER_KEY) {
          return (
            <div className="flex items-center gap-2 px-2 py-1 text-brand-500">
              {kit.dragHandle}
              <span className="text-sm font-semibold">{divider?.label}</span>
              <span
                aria-hidden
                className="h-px min-w-0 flex-1 bg-current opacity-40"
              />
            </div>
          );
        }
        const key = item.key;
        const first = !divider || picked.indexOf(key) < divider.at;
        const fieldLabel = labelOf(key);
        return (
          <DndList.Row
            className={cn(
              'rounded-lg py-1 pr-1 pl-2',
              first ? 'font-medium' : 'text-fg-muted'
            )}
          >
            <div className="flex min-w-0 items-center gap-1">
              <span className="min-w-0 flex-1 truncate">{fieldLabel}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                layout="icon"
                aria-label={t('remove', { field: fieldLabel })}
                onClick={() => onRemove(key)}
              >
                <X />
              </Button>
            </div>
          </DndList.Row>
        );
      }}
    </DndList>
  );
}
